import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useSwitch, useViewport } from '@chilecompra/react-kit'
import { Card, DataTable, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import truncate from '../../modules/utils/limiter'
import generateYearsBetween from '../../modules/utils/years'
import TreemapChart from '../../components/Charts/TreemapChart'
import RegionalItemsAndProducts from '../../components/Infographics/RegionalItemsAndProducts'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { onGetProductsServicesThunk } from './ContingenciesProductsServicesSection.actions'
import {
  CategoryIcon,
  Content,
  FamilyIcon,
  GridContainer,
  GridItem,
  ProductIcon,
  Root,
  SegmentIcon
} from './ContingenciesProductsServicesSection.styles'

const NEXT_TYPES_DICTIONARY = {
  categories: 'products',
  families: 'categories',
  initial: 'segments',
  segments: 'families'
}

const TYPE_ICONS_DICTIONARY = {
  categories: <CategoryIcon />,
  families: <FamilyIcon />,
  products: <ProductIcon />,
  segments: <SegmentIcon />
}

/**
 * The ContingenciesProductsServicesSection's container.
 */
const ContingenciesProductsServicesSection = () => {
  const dispatch = useDispatch()
  const { latestUpdate, loading } = useSelector(state => state.topNavbar)
  const { csv, data } = useSelector(state => state.contingenciesProductsServices)
  const {
    size: { isUpToMedium }
  } = useViewport()

  const { contingencyOptions, contingencySelected } = useSelector(state => state.contingencies)
  const [startYear, setStartYear] = useState(undefined)
  const [endYear, setEndYear] = useState(undefined)
  const getYearOptions = contingencyOptions?.find(option => option.value === contingencySelected)

  const [levels, setLevels] = useState([])
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => {
      dispatch(
        onGetProductsServicesThunk({
          contingencyId: contingencySelected,
          type: NEXT_TYPES_DICTIONARY.initial,
          year: updatedValue,
          callback: () => setLevels([])
        })
      )
    },
    overwriteCallback: () => endYear
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('products-services-treemap-chart', `Info_Contingencias_Productos_Servicios_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Contingencias_Productos_Servicios_${yearValue}`)
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const handleChartLabelFormatter = useCallback(
    function () {
      return `
        <div style="position: relative; white-space: initial; width: 100%;">
          ${this.point.value >= 8 ? this.point.label : truncate(this.point.label, 45, ' ...')}
          <br />
          
          <strong>
          ${currencyValue ? CURRENCY_VALUES.USD : CURRENCY_VALUES.CLP}${formatAmount(
            currencyValue ? this.point.amountUSD : this.point.amount
          )}
          </strong>
        </div>
      `
    },
    [currencyValue]
  )
  const handleClickMap = useCallback(
    (type, label, categoryId, familyId, productId, segmentId) => {
      if (['categories', 'families', 'segments'].indexOf(type) === -1) {
        return
      }

      dispatch(
        onGetProductsServicesThunk({
          categoryId,
          contingencyId: contingencySelected,
          familyId,
          productId,
          segmentId,
          type: NEXT_TYPES_DICTIONARY[type],
          year: yearValue,
          callback: () =>
            setLevels(currentLevels => [
              ...currentLevels,
              {
                categoryId,
                familyId,
                productId,
                segmentId,
                icon: TYPE_ICONS_DICTIONARY[NEXT_TYPES_DICTIONARY[type]],
                name: label,
                type: NEXT_TYPES_DICTIONARY[type]
              }
            ])
        })
      )
    },
    [yearValue]
  )
  const handleClickTree = useCallback(
    (item, index) => {
      setLevels(currentCollection => currentCollection.slice(0, index + 1))
      dispatch(
        onGetProductsServicesThunk({
          categoryId: item.categoryId,
          contingencyId: contingencySelected,
          familyId: item.familyId,
          segmentId: item.segmentId,
          year: yearValue,
          type: item.type
        })
      )
    },
    [yearValue]
  )
  const handleReset = useCallback(() => {
    setLevels([])
    dispatch(
      onGetProductsServicesThunk({
        contingencyId: contingencySelected,
        type: NEXT_TYPES_DICTIONARY.initial,
        year: yearValue
      })
    )
  }, [yearValue])

  useEffect(() => {
    setStartYear(getYearOptions?.startYear)
    setEndYear(getYearOptions?.endYear)
  }, [getYearOptions])

  useEffect(() => {
    dispatch(
      onGetProductsServicesThunk({
        contingencyId: contingencySelected,
        type: NEXT_TYPES_DICTIONARY.initial,
        year: yearValue
      })
    )
  }, [yearValue])

  return (
    <Root>
      <Content>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <Typography
              color="primary"
              fontSize="18px"
              fontWeight="bold"
              lineHeight="21px"
              margin={isUpToMedium ? '96px 0 0' : '48px 0 24px'}
              variant="body1"
            >
              Compras por COVID-19 en Mercado Público
            </Typography>
            <Typography
              fontSize="34px"
              fontWeight="bold"
              lineHeight="38px"
              margin={isUpToMedium ? '12px 0' : '0'}
              variant="h2"
              width={isUpToMedium ? '485px' : undefined}
            >
              Productos y servicios más transados por COVID-19
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={isUpToMedium ? 5 : 12}>
            <Typography
              fontSize="18px"
              lineHeight="24px"
              margin={isUpToMedium ? '0' : '24px 0'}
              variant="body1"
              width={isUpToMedium ? '448px' : undefined}
            >
              Revisa en detalle los montos que se han transado en los distintos rubros por motivo COVID-19.
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer margin="24px 0 0">
          <GridItem xs={12}>
            <DataLayout
              disableCaptureImage={disableCaptureImage}
              disableDownloadFile={disableDownloadFile}
              header={
                <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                  <GridItem md={2} xs={12}>
                    <Select
                      label="AÑO"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={2} xs={12}>
                    <Switch
                      checked={currencyValue}
                      color="primary"
                      labelLeft="CLP"
                      labelRight="USD"
                      margin="0 0 0 30px"
                      onChange={handleChangeCurrency}
                    />
                  </GridItem>
                </GridContainer>
              }
              infographic={<RegionalItemsAndProducts />}
              marginFooter="0 0 96px"
              noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
              ref={dataLayoutRef}
              updatedDate={latestUpdate}
              onCaptureImage={handleCaptureImage}
              onChangeView={handleChangeView}
              onDownloadFile={handleDownloadFile}
            >
              <DataLayoutChild view={DataLayoutViews.CHART}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer>
                    <GridItem xs={12}>
                      <TreemapChart
                        data={data}
                        id="products-services-treemap-chart"
                        levels={levels}
                        rootIcon={<SegmentIcon />}
                        rootName="Estado de Chile"
                        labelFormatter={handleChartLabelFormatter}
                        onClickMap={handleClickMap}
                        onClickTree={handleClickTree}
                        onReset={handleReset}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer padding="0 0 24px">
                    <GridItem xs={12}>
                      <DataTable
                        headers={[
                          { label: 'Nombre rubro', value: 'label' },
                          { label: 'Monto total (CLP)', value: 'amount' },
                          { label: 'Monto total (USD)', value: 'amountUSD' }
                        ]}
                        headersCellsProps={[
                          { enableSort: true, width: '60%' },
                          { enableSort: true },
                          { enableSort: true }
                        ]}
                        initialRows={6}
                        loading={loading}
                        records={data}
                        recordsDecorators={[
                          ({ label }) => label,
                          ({ amount }) => `$${formatAmount(amount)}`,
                          ({ amountUSD }) => `$${formatAmount(amountUSD)}`
                        ]}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default ContingenciesProductsServicesSection
